import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PageShell } from "@/components/layout/PageShell";
import { IntakeStepHeader } from "@/components/intake/IntakeStepHeader";
import { IntakeProgress } from "@/components/intake/IntakeProgress";
import { QuestionCard } from "@/components/intake/QuestionCard";
import { EvidenceMapper } from "@/components/intake/EvidenceMapper";
import { IntakeNav } from "@/components/intake/IntakeNav";
import { SaveStatusPill } from "@/components/layout/SaveStatusPill";
import {
  SECTIONS,
  PROVIDER_INTAKE_SECTIONS,
  QUESTIONNAIRE_SECTIONS,
  isProviderIntakeComplete
} from "@/lib/questionnaire";
import { useAssessment } from "@/context/AssessmentContext";

function isAnswered(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function requiredIdsForSection(section) {
  if (section.customComponent === "EvidenceMapper") {
    return (section.items || []).map((i) => i.id);
  }
  return section.questions.filter((q) => !q.optional).map((q) => q.id);
}

// Phase 8B — the same component now services three routes:
//   /intake         -> mode="intake"         (Sections 01-04)
//   /questionnaire  -> mode="questionnaire"  (Sections 05-09)
//   /intake?all=1   -> mode="all"            (legacy combined flow, testing only)
// The combined-flow path is preserved deliberately so any older saved
// assessment can be re-opened byte-identically; it is not linked from the
// public UI.
export default function IntakePage({ mode = "intake" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    answers,
    setAnswer,
    completeAssessment,
    markProviderIntakeComplete,
    providerIntakeCompletedAt
  } = useAssessment();

  // Legacy escape hatch — keeps historical /intake?all=1 URLs working.
  const combined = mode === "all" || new URLSearchParams(location.search).get("all") === "1";
  const effectiveMode = combined ? "all" : mode;

  const sections = useMemo(() => {
    if (effectiveMode === "intake") return PROVIDER_INTAKE_SECTIONS;
    if (effectiveMode === "questionnaire") return QUESTIONNAIRE_SECTIONS;
    return SECTIONS;
  }, [effectiveMode]);

  // Phase 8C.1 — sequential visible numbering for Questionnaire questions.
  // Provider Intake keeps the section-relative "01.01" format unchanged.
  // The map is built once per mode and applied consistently to QuestionCard
  // and EvidenceMapper without touching question IDs or storage keys.
  const displayNumberMap = useMemo(() => {
    if (effectiveMode !== "questionnaire") return null;
    const map = {};
    let counter = 0;
    for (const s of sections) {
      if (s.customComponent === "EvidenceMapper") {
        for (const it of s.items || []) {
          counter += 1;
          map[it.id] = counter;
        }
      } else {
        for (const q of s.questions || []) {
          counter += 1;
          map[q.id] = counter;
        }
      }
    }
    return map;
  }, [effectiveMode, sections]);
  const displayNumberFor = displayNumberMap ? (id) => displayNumberMap[id] : undefined;

  // Guard: entering /questionnaire without a complete Provider Intake bounces
  // the user back to /intake. Legacy saved assessments that already contain
  // full intake answers are treated as "intake complete" and admitted.
  useEffect(() => {
    if (effectiveMode !== "questionnaire") return;
    if (isProviderIntakeComplete(answers) || providerIntakeCompletedAt) return;
    toast.info("Provider Intake needs to be completed first.");
    navigate("/intake", { replace: true });
  }, [effectiveMode, answers, providerIntakeCompletedAt, navigate]);

  const [stepIndex, setStepIndex] = useState(0);
  const section = sections[stepIndex] || sections[0];

  const completionMap = useMemo(() => {
    const map = {};
    sections.forEach((s) => {
      const ids = requiredIdsForSection(s);
      const answered = ids.filter((id) => isAnswered(answers[id])).length;
      map[s.id] = { answered, required: ids.length };
    });
    return map;
  }, [answers, sections]);

  const requiredIds = requiredIdsForSection(section);
  const missingNaReasons =
    section.customComponent === "EvidenceMapper"
      ? requiredIds.filter((id) => answers[id] === "na" && !isAnswered(answers[`${id}__na_reason`]))
      : [];
  const canAdvance =
    requiredIds.every((id) => isAnswered(answers[id])) && missingNaReasons.length === 0;

  const totalRequired = sections.reduce(
    (acc, s) => acc + requiredIdsForSection(s).length,
    0
  );
  const answeredRequired = sections.reduce(
    (acc, s) => acc + requiredIdsForSection(s).filter((id) => isAnswered(answers[id])).length,
    0
  );
  const progressPct = totalRequired === 0 ? 0 : (answeredRequired / totalRequired) * 100;

  const handleBack = () => {
    if (stepIndex === 0) return;
    setStepIndex((s) => s - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNext = () => {
    if (!canAdvance) {
      toast.error(
        missingNaReasons.length > 0
          ? "Each item marked Not applicable needs a brief explanation before continuing."
          : "Please answer the required items in this section before continuing."
      );
      return;
    }
    if (stepIndex < sections.length - 1) {
      setStepIndex((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // End of the current stage.
    if (effectiveMode === "intake") {
      markProviderIntakeComplete();
      navigate("/intake/complete");
    } else {
      // questionnaire (or legacy combined) — this is the true assessment finish.
      completeAssessment();
      navigate("/results");
    }
  };

  const progressLabel =
    effectiveMode === "intake"
      ? "Provider Intake progress"
      : effectiveMode === "questionnaire"
      ? "SIL readiness check progress"
      : "Intake progress";

  const stageIntro =
    effectiveMode === "intake"
      ? "Tell us about your organisation, service model and current registration position."
      : effectiveMode === "questionnaire"
      ? "Continue through evidence, governance and what to look at next."
      : null;

  return (
    <PageShell hideFooter>
      <div className="container py-10 lg:py-14">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          {stageIntro ? (
            <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground" data-testid="stage-intro">
              {stageIntro}
            </p>
          ) : (
            <span />
          )}
          <SaveStatusPill />
        </div>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-3">
            <IntakeProgress
              sections={sections}
              currentIndex={stepIndex}
              completionMap={completionMap}
              heading={progressLabel}
            />
          </div>
          <div className="lg:col-span-9">
            <IntakeStepHeader
              section={section}
              currentIndex={stepIndex}
              totalSections={sections.length}
              progressPct={progressPct}
            />
            <div className="mt-8">
              {section.customComponent === "EvidenceMapper" ? (
                <EvidenceMapper
                  section={section}
                  answers={answers}
                  onChange={setAnswer}
                  displayNumberFor={displayNumberFor}
                />
              ) : (
                <div className="space-y-5">
                  {section.questions.map((question, idx) => (
                    <QuestionCard
                      key={question.id}
                      section={section}
                      question={question}
                      index={idx}
                      value={answers[question.id]}
                      onChange={(value) => setAnswer(question.id, value)}
                      displayNumber={displayNumberFor ? displayNumberFor(question.id) : undefined}
                    />
                  ))}
                </div>
              )}
            </div>
            <IntakeNav
              onBack={handleBack}
              onNext={handleNext}
              isFirst={stepIndex === 0}
              isLast={stepIndex === sections.length - 1}
              canAdvance={canAdvance}
              hint={
                missingNaReasons.length > 0
                  ? "Each Not applicable item needs a brief explanation to continue"
                  : undefined
              }
            />
          </div>
        </div>
      </div>
    </PageShell>
  );
}
